/**
 * Everspring — Page transitions
 *
 * Three-layer page navigation feedback:
 *
 * 1. Top progress bar (Firefox / older browsers only).
 *    Rendered server-side as <div id="es-page-progress"> in theme.liquid.
 *    On click of a same-origin link we wait 180ms (so fast loads don't
 *    flash), then animate the bar to ~82% while the page loads. The new
 *    page renders a fresh bar; on its first paint we smoothly animate
 *    0 → 100 and fade out. On browsers that support cross-document
 *    View Transitions API, we SKIP the bar entirely — the native fade
 *    already gives feedback and two layers of motion read as jank.
 *
 * 2. View Transitions API (Chrome/Edge/Safari 18+).
 *    Native cross-document fade is opted-in via CSS (@view-transition).
 *    For matching elements between collection and PDP we set
 *    view-transition-name in the card and gallery snippets so the product
 *    image visually morphs from the card into the PDP hero.
 *
 * 3. Link prefetch on hover/touchstart (all modern browsers).
 *    When the user hovers an internal link for >60ms we inject a
 *    <link rel="prefetch"> into the head so the browser warms its HTTP
 *    cache. By the time the user actually clicks, the next page's HTML
 *    is often already in memory — first paint is near-instant, leaving
 *    almost no room for visual flicker. Respects Save-Data and slow
 *    connection hints.
 *
 * Excluded from triggering the bar / prefetch:
 *  - external links, mailto:, tel:, javascript:, hash-only links
 *  - links with target=_blank or download attribute
 *  - cmd/ctrl/shift/alt-clicks (open in new tab/window)
 *  - cart add-to-cart forms (those use XHR, no navigation)
 */
(function () {
  'use strict';

  /* ──────────────────────────────────────────────────────────────────
     Shared: same-origin internal navigation link detection
     ────────────────────────────────────────────────────────────────── */
  function isInternalNavigationLink(a) {
    if (!a || !a.href) return false;
    if (a.target && a.target !== '' && a.target !== '_self') return false;
    if (a.hasAttribute('download')) return false;
    var href = a.getAttribute('href');
    if (!href) return false;
    if (href.charAt(0) === '#') return false;
    if (/^(mailto:|tel:|javascript:|sms:)/i.test(href)) return false;
    try {
      var url = new URL(a.href, window.location.href);
      if (url.origin !== window.location.origin) return false;
      // Same path + same query = no navigation, just hash change
      if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash) return false;
      return true;
    } catch (e) { return false; }
  }

  /* ──────────────────────────────────────────────────────────────────
     PROGRESS BAR — only on browsers without cross-document View Transitions

     When the browser supports the View Transitions API, its native
     cross-fade already gives feedback. Layering the progress bar on top
     means two separate motions running in parallel, which reads as
     jank. We early-return here so the bar only runs as a fallback on
     Firefox and older browsers.
     ────────────────────────────────────────────────────────────────── */
  // In-app browsers get no view transitions (see theme.liquid), so they
  // fall back to the progress bar.
  var supportsViewTransitions =
    !window.__esInAppBrowser &&
    typeof document !== 'undefined' &&
    'startViewTransition' in document &&
    typeof CSS !== 'undefined' &&
    CSS.supports &&
    CSS.supports('selector(::view-transition-old(root))');

  if (!supportsViewTransitions) {
    initProgressBar();
  }

  function initProgressBar() {
    var bar = document.getElementById('es-page-progress');
    if (!bar) return;

    var SHOW_DELAY_MS = 180;       // wait this long before showing bar
    var TRICKLE_TARGET = 0.82;     // bar climbs to ~82% during navigation
    var TRICKLE_DURATION = 8000;   // ms it takes to ease there
    var RESTORE_DURATION = 320;    // ms to smoothly go 0 → 100 on new page
    var FADE_DURATION = 240;       // ms to fade out after finish

    var showTimer = null;
    var trickleStart = 0;
    var rafId = null;
    var isFinished = false;

    function setProgress(p) {
      bar.style.transform = 'scaleX(' + p + ')';
    }

    function show() {
      bar.classList.add('is-visible');
      bar.classList.remove('is-finishing');
      bar.style.opacity = '1';
      setProgress(0.04);
      trickleStart = performance.now();
      tickTrickle();
    }

    function tickTrickle() {
      if (isFinished) return;
      var elapsed = performance.now() - trickleStart;
      var t = Math.min(elapsed / TRICKLE_DURATION, 1);
      var eased = 1 - Math.pow(1 - t, 3);
      var p = 0.04 + (TRICKLE_TARGET - 0.04) * eased;
      setProgress(p);
      if (t < 1) {
        rafId = requestAnimationFrame(tickTrickle);
      }
    }

    function finishImmediate() {
      if (showTimer) { clearTimeout(showTimer); showTimer = null; }
      if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
      if (!bar.classList.contains('is-visible')) return;
      isFinished = true;
      bar.classList.add('is-finishing');
      setProgress(1);
      setTimeout(function () {
        bar.style.opacity = '0';
        setTimeout(function () {
          bar.classList.remove('is-visible');
          bar.classList.remove('is-finishing');
          setProgress(0);
          isFinished = false;
        }, FADE_DURATION);
      }, 220);
    }

    function startNavigation() {
      if (showTimer) clearTimeout(showTimer);
      showTimer = setTimeout(show, SHOW_DELAY_MS);
    }

    document.addEventListener('click', function (e) {
      if (e.defaultPrevented) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      if (e.button !== 0) return;
      var a = e.target.closest && e.target.closest('a[href]');
      if (!isInternalNavigationLink(a)) return;
      startNavigation();
    }, true);

    // Stitch the bar across navigation: if a previous page set a
    // sessionStorage flag, smoothly animate 0 → 100 over RESTORE_DURATION
    // on the new page. No more 0 → 85 jump that read as a visible flash.
    try {
      var inflight = sessionStorage.getItem('es-pt-inflight');
      if (inflight) {
        sessionStorage.removeItem('es-pt-inflight');
        requestAnimationFrame(function () {
          bar.classList.add('is-visible');
          bar.style.opacity = '1';
          setProgress(0);
          // Force a layout commit at 0 before animating to 1 so the
          // default transform transition kicks in (250ms per base CSS).
          bar.getBoundingClientRect();
          setProgress(1);
          setTimeout(function () {
            bar.style.opacity = '0';
            setTimeout(function () {
              bar.classList.remove('is-visible');
              setProgress(0);
            }, FADE_DURATION);
          }, RESTORE_DURATION);
        });
      }
    } catch (e) {}

    // FIX (Android in-app browsers): dit was een 'beforeunload'-listener.
    // Zodra een pagina een beforeunload-listener heeft, moet de browser bij
    // elke navigatie de unload-prompt-flow doorlopen. In een Android WebView
    // gaat die via WebChromeClient.onJsBeforeUnload() naar de host-app.
    // In-app browsers (Instagram, Facebook) onderdrukken die dialoog vaak
    // door true terug te geven zonder result.confirm() aan te roepen, en dan
    // wordt de navigatie STILZWIJGEND geannuleerd. Symptoom: je tikt op een
    // link en er gebeurt niets, terwijl alles in Chrome en op iOS werkt.
    //
    // 'pagehide' doet hier precies hetzelfde werk, vuurt in alle browsers,
    // en triggert de unload-prompt-flow niet.
    window.addEventListener('pagehide', function () {
      try { sessionStorage.setItem('es-pt-inflight', '1'); } catch (e) {}
    });

    // BFCache restore (back button) — finish any visible bar
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) {
        finishImmediate();
      }
    });
  }

  /* ──────────────────────────────────────────────────────────────────
     LINK PREFETCH on hover / touchstart

     When the user hovers (or taps on mobile) an internal link, we wait
     60ms — enough to avoid prefetching for quick fly-overs — then inject
     a <link rel="prefetch" as="document"> so the browser can warm its
     cache. By the time the actual click fires, the next page's HTML is
     often already in memory and its first paint is near-instant,
     leaving almost no room for visual flicker.

     Skipped on:
       - Save-Data header enabled
       - Slow connections (2g / slow-2g)
       - Links already prefetched in this page load (deduplicated)
     ────────────────────────────────────────────────────────────────── */
  initPrefetch();

  function initPrefetch() {
    // Respect user bandwidth preferences
    var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (conn) {
      if (conn.saveData) return;
      if (conn.effectiveType === '2g' || conn.effectiveType === 'slow-2g') return;
    }

    // Feature check: rel=prefetch support
    var linkEl = document.createElement('link');
    var relList = linkEl.relList;
    if (!relList || !relList.supports || !relList.supports('prefetch')) return;

    var prefetched = new Set();
    var hoverTimer = null;
    var HOVER_DELAY_MS = 60;
    var MAX_PREFETCH = 12; // soft cap per page load

    function prefetchUrl(url) {
      if (prefetched.size >= MAX_PREFETCH) return;
      if (prefetched.has(url)) return;
      prefetched.add(url);
      var link = document.createElement('link');
      link.rel = 'prefetch';
      link.as = 'document';
      link.href = url;
      // NOTE: crossOrigin = 'anonymous' was removed. These are same-origin
      // URLs, and an anonymous prefetch is fetched without credentials, so
      // Chrome cannot reuse it for the real (credentialed) navigation and
      // refetches. Worst case on Android it competes with the pending
      // navigation instead of speeding it up.
      document.head.appendChild(link);
    }

    function onLinkEnter(e) {
      var a = e.target.closest && e.target.closest('a[href]');
      if (!isInternalNavigationLink(a)) return;
      if (hoverTimer) clearTimeout(hoverTimer);
      var url = a.href;
      hoverTimer = setTimeout(function () {
        prefetchUrl(url);
      }, HOVER_DELAY_MS);
    }

    function onLinkLeave() {
      if (hoverTimer) { clearTimeout(hoverTimer); hoverTimer = null; }
    }

    // Desktop: mouseenter/mouseleave via event delegation (using pointerover
    // since mouseenter doesn't bubble). Mobile: touchstart catches the tap
    // milliseconds before the click fires, giving the browser a head start.
    document.addEventListener('pointerover', onLinkEnter, { passive: true });
    document.addEventListener('pointerout', onLinkLeave, { passive: true });
    document.addEventListener('touchstart', function (e) {
      var a = e.target.closest && e.target.closest('a[href]');
      if (!isInternalNavigationLink(a)) return;
      prefetchUrl(a.href);
    }, { passive: true });
  }
})();
